import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Divider,
  Alert,
  IconButton,
  LinearProgress,
  Card,
  CardContent,
  TextField,
  List,
  ListItem,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import BarcodeScanner from 'src/components/TransferOrder/BarcodeScanner';
import StatusBadge from 'src/components/TransferOrder/StatusBadge';
import Loader from 'src/components/Loader';
import { debounce } from 'lodash';
import {
  TransferOrder,
  TransferOrderStatus,
  TransferOrderRecord,
  ReceivedItemState,
} from 'src/types/TransferOrder.types';
import { TransferOrderService } from 'src/services/transfer-order-service';
import { toast } from 'react-toastify';

const ReceiveOrder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [currentOrder, setCurrentOrder] = useState<TransferOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  const [receivedItems, setReceivedItems] = useState<ReceivedItemState[]>([]);
  const [showQuantityDialog, setShowQuantityDialog] = useState(false);
  const [showDiscrepancyDialog, setShowDiscrepancyDialog] = useState(false);
  const [currentScan, setCurrentScan] = useState<{
    barcode: string;
    record: TransferOrderRecord;
  } | null>(null);
  const [quantityInput, setQuantityInput] = useState('');
  const [condition, setCondition] = useState<'Good' | 'Damaged'>('Good');
  const [discrepancyNotes, setDiscrepancyNotes] = useState('');

  const fetchOrder = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const order = await TransferOrderService.getById(id);
      setCurrentOrder(order);
    } catch (error: any) {
      console.error('Failed to fetch transfer order:', error);
      const errorMessage = error?.message || 'Failed to load transfer order';
      dispatch(showAlert({ message: errorMessage, type: 'error' }));
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [id]);

  // Initialize received items state from order records
  useEffect(() => {
    if (currentOrder?.Records) {
      const items: ReceivedItemState[] = currentOrder.Records.map((record) => ({
        barcode: record.barcode,
        expectedQuantity: record.shippedQuantity,
        receivedQuantity: record.receivedQuantity,
        condition: record.receivedQuantity === 0 ? 'Pending' : 'Good',
        scannedAt: null,
        notes: '',
        status:
          record.receivedQuantity === 0
            ? 'pending'
            : record.receivedQuantity < record.shippedQuantity
            ? 'partial'
            : 'complete',
      }));
      setReceivedItems(items);
    }
  }, [currentOrder]);

  // Auto-save progress (debounced)
  const autoSaveProgress = useMemo(
    () =>
      debounce(async (items: ReceivedItemState[]) => {
        if (!currentOrder || items.length === 0) return;

        const itemsToReceive = items
          .filter((item) => item.receivedQuantity > 0)
          .map((item) => ({
            barcode: item.barcode,
            receivedQuantity: item.receivedQuantity,
            condition: item.condition === 'Damaged' ? ('Damaged' as const) : ('Good' as const),
            notes: item.notes,
          }));

        if (itemsToReceive.length === 0) return;

        try {
          await TransferOrderService.receive({
            transferOrderId: currentOrder.Id,
            items: itemsToReceive,
            isComplete: false,
            notes: 'Auto-saved progress',
          });
        } catch (error) {
          console.error('Auto-save failed:', error);
        }
      }, 3000),
    [currentOrder]
  );

  useEffect(() => {
    // Auto-save when receivedItems change
    if (receivedItems.some((item) => item.receivedQuantity > 0)) {
      autoSaveProgress(receivedItems);
    }

    return () => {
      autoSaveProgress.cancel();
    };
  }, [receivedItems, autoSaveProgress]);

  const handleScan = useCallback(
    async (barcode: string) => {
      if (!currentOrder?.Records) return;

      // Find the record for this barcode
      const record = currentOrder.Records.find((r) => r.barcode === barcode);

      if (!record) {
        dispatch(
          showAlert({
            message: `Barcode ${barcode} not expected in this order`,
            type: 'error',
          })
        );
        return;
      }

      // Check if already fully received
      const receivedItem = receivedItems.find((p) => p.barcode === barcode);
      if (receivedItem && receivedItem.status === 'complete') {
        dispatch(
          showAlert({
            message: `${barcode} already fully received`,
            type: 'warning',
          })
        );
        return;
      }

      // Show quantity dialog with expected quantity pre-filled
      setCurrentScan({ barcode, record });
      const remaining = record.shippedQuantity - record.receivedQuantity;
      setQuantityInput(String(remaining));
      setCondition('Good');
      setDiscrepancyNotes('');
      setShowQuantityDialog(true);
    },
    [currentOrder, receivedItems, dispatch]
  );

  const handleConfirmQuantity = () => {
    if (!currentScan) return;

    const quantity = parseInt(quantityInput, 10);
    if (isNaN(quantity) || quantity <= 0) {
      dispatch(showAlert({ message: 'Please enter a valid quantity', type: 'error' }));
      return;
    }

    const { barcode, record } = currentScan;
    const remaining = record.shippedQuantity - record.receivedQuantity;

    if (quantity > remaining) {
      dispatch(
        showAlert({
          message: `Cannot receive more than shipped. Remaining: ${remaining}`,
          type: 'error',
        })
      );
      return;
    }

    // Check for discrepancy
    if (quantity < remaining || condition === 'Damaged') {
      setShowQuantityDialog(false);
      setShowDiscrepancyDialog(true);
      return;
    }

    // Update received items
    updateReceivedItem(barcode, quantity, condition, '');
  };

  const handleConfirmDiscrepancy = () => {
    if (!currentScan) return;

    const quantity = parseInt(quantityInput, 10);
    const { barcode } = currentScan;

    updateReceivedItem(barcode, quantity, condition, discrepancyNotes);
    setShowDiscrepancyDialog(false);
  };

  const updateReceivedItem = (
    barcode: string,
    quantity: number,
    itemCondition: 'Good' | 'Damaged',
    notes: string
  ) => {
    setReceivedItems((prev) =>
      prev.map((item) => {
        if (item.barcode === barcode) {
          const newReceivedQty = item.receivedQuantity + quantity;
          return {
            ...item,
            receivedQuantity: newReceivedQty,
            condition: itemCondition,
            scannedAt: new Date().toISOString(),
            notes,
            status:
              newReceivedQty === 0
                ? 'pending'
                : newReceivedQty < item.expectedQuantity
                ? 'partial'
                : 'complete',
          };
        }
        return item;
      })
    );

    dispatch(
      showAlert({
        message: `Received ${quantity} units of ${barcode}`,
        type: 'success',
      })
    );

    setShowQuantityDialog(false);
    setCurrentScan(null);
    setQuantityInput('');
    setCondition('Good');
    setDiscrepancyNotes('');
  };

  const handleCompleteReceiving = async () => {
    if (!currentOrder) return;

    const itemsToReceive = receivedItems
      .filter((item) => item.receivedQuantity > 0)
      .map((item) => ({
        barcode: item.barcode,
        receivedQuantity: item.receivedQuantity,
        condition: item.condition === 'Damaged' ? ('Damaged' as const) : ('Good' as const),
        notes: item.notes,
      }));

    if (itemsToReceive.length === 0) {
      dispatch(showAlert({ message: 'No items received yet', type: 'warning' }));
      toast.warning('No items received yet');
      return;
    }

    setOperationLoading(true);
    try {
      const result = await TransferOrderService.receive({
        transferOrderId: currentOrder.Id,
        items: itemsToReceive,
        isComplete: true,
        notes: 'Receiving completed',
      });

      dispatch(
        showAlert({
          message: result.message || 'Order received successfully',
          type: 'success',
        })
      );
      toast.success(result.message || 'Order received successfully');

      // Navigate to order history or dashboard
      navigate('/client/orders');
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to complete receiving';
      dispatch(showAlert({ message: errorMessage, type: 'error' }));
      toast.error(errorMessage);
    } finally {
      setOperationLoading(false);
    }
  };

  const progress = useMemo(() => {
    if (receivedItems.length === 0) return { received: 0, total: 0, percentage: 0 };

    const received = receivedItems.filter((item) => item.status === 'complete').length;
    const total = receivedItems.length;
    const percentage = (received / total) * 100;

    return { received, total, percentage };
  }, [receivedItems]);

  const totalQuantity = useMemo(() => {
    const expected = receivedItems.reduce((sum, item) => sum + item.expectedQuantity, 0);
    const received = receivedItems.reduce((sum, item) => sum + item.receivedQuantity, 0);
    return { expected, received, percentage: expected > 0 ? (received / expected) * 100 : 0 };
  }, [receivedItems]);

  const nextItemToReceive = useMemo(() => {
    return receivedItems.find((item) => item.status !== 'complete');
  }, [receivedItems]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Loader />
      </Box>
    );
  }

  if (!currentOrder) {
    return (
      <Box>
        <Alert severity="error">Transfer order not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/client/orders')} sx={{ mt: 2 }}>
          Back to Orders
        </Button>
      </Box>
    );
  }

  if (
    currentOrder.Status !== TransferOrderStatus.InTransit 
    // currentOrder.Status !== TransferOrderStatus.Receiving
  ) {
    return (
      <Box>
        <Alert severity="warning">
          This order is not ready for receiving. Current status:{' '}
          <StatusBadge status={currentOrder.Status} type="order" />
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/client/orders')} sx={{ mt: 2 }}>
          Back to Orders
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate('/client/orders')}>
          <ArrowBackIcon />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h4" fontWeight={700}>
            Receive Order
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {currentOrder.OrderName || `Order #${currentOrder.Id.slice(0, 8)}`}
          </Typography>
        </Box>
        <StatusBadge status={currentOrder.Status} type="order" size="medium" />
      </Stack>

      {/* Progress Card */}
      <Card sx={{ mb: 3, bgcolor: 'success.50' }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={600}>
                Progress: {progress.received}/{progress.total} items
              </Typography>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {Math.round(progress.percentage)}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progress.percentage}
              color="success"
              sx={{ height: 10, borderRadius: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              Quantity: {totalQuantity.received}/{totalQuantity.expected} units (
              {Math.round(totalQuantity.percentage)}%)
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {/* Barcode Scanner */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          Scan Barcode
        </Typography>
        <BarcodeScanner onScan={handleScan} placeholder="Scan or type barcode" loading={operationLoading} />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Scan items as you receive them. Progress is automatically saved.
        </Typography>
      </Paper>

      {/* Next Item to Receive */}
      {nextItemToReceive && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'info.50', borderLeft: 4, borderColor: 'info.main' }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <InventoryIcon color="info" />
            <Typography variant="h6" fontWeight={600}>
              Next Item Expected
            </Typography>
          </Stack>
          <Stack spacing={1}>
            <Typography variant="body1" fontFamily="monospace" fontWeight={700} fontSize={18}>
              {nextItemToReceive.barcode}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Expected: <strong>{nextItemToReceive.expectedQuantity}</strong> units
            </Typography>
            {nextItemToReceive.receivedQuantity > 0 && (
              <Typography variant="body2" color="text.secondary">
                Already Received: <strong>{nextItemToReceive.receivedQuantity}</strong> units
              </Typography>
            )}
          </Stack>
        </Paper>
      )}

      {/* Received Items List */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          Expected Items
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <List>
          {receivedItems.map((item, index) => (
            <ListItem
              key={index}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                bgcolor:
                  item.status === 'complete'
                    ? 'success.50'
                    : item.condition === 'Damaged'
                    ? 'error.50'
                    : 'background.paper',
              }}
              secondaryAction={
                item.status === 'complete' ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    {item.condition === 'Damaged' && <Chip label="Damaged" size="small" color="error" />}
                    <CheckCircleIcon color="success" />
                  </Stack>
                ) : (
                  <Chip
                    label={item.status === 'partial' ? 'Partial' : 'Pending'}
                    size="small"
                    color={item.status === 'partial' ? 'warning' : 'default'}
                  />
                )
              }
            >
              <ListItemText
                primary={
                  <Typography variant="body1" fontFamily="monospace" fontWeight={600}>
                    {item.barcode}
                  </Typography>
                }
                secondary={
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      Received: {item.receivedQuantity}/{item.expectedQuantity}
                    </Typography>
                    {item.scannedAt && (
                      <Typography variant="caption" color="text.secondary">
                        Scanned: {new Date(item.scannedAt).toLocaleTimeString()}
                      </Typography>
                    )}
                    {item.notes && (
                      <Typography variant="caption" color="text.secondary">
                        Notes: {item.notes}
                      </Typography>
                    )}
                  </Stack>
                }
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Action Buttons */}
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button variant="outlined" onClick={() => navigate('/client/orders')}>
          Save & Exit
        </Button>
        <Button
          variant="contained"
          startIcon={<CheckCircleIcon />}
          onClick={handleCompleteReceiving}
          disabled={operationLoading || progress.received === 0}
          size="large"
          color="success"
        >
          Complete Receiving
        </Button>
      </Stack>

      {/* Quantity Dialog */}
      <Dialog open={showQuantityDialog} onClose={() => setShowQuantityDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Received Quantity</DialogTitle>
        <DialogContent>
          {currentScan && (
            <>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Barcode: <strong>{currentScan.barcode}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Expected: <strong>{currentScan.record.shippedQuantity}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                Already Received: <strong>{currentScan.record.receivedQuantity}</strong>
              </Typography>
              <TextField
                fullWidth
                type="number"
                label="Quantity Received"
                value={quantityInput}
                onChange={(e) => setQuantityInput(e.target.value)}
                inputProps={{
                  min: 1,
                  max: currentScan.record.shippedQuantity - currentScan.record.receivedQuantity,
                }}
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmQuantity();
                  }
                }}
                sx={{ mb: 2 }}
              />
              <FormControl component="fieldset">
                <FormLabel component="legend">Condition</FormLabel>
                <RadioGroup value={condition} onChange={(e) => setCondition(e.target.value as 'Good' | 'Damaged')}>
                  <FormControlLabel value="Good" control={<Radio />} label="Good" />
                  <FormControlLabel value="Damaged" control={<Radio />} label="Damaged" />
                </RadioGroup>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowQuantityDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmQuantity}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discrepancy Dialog */}
      <Dialog open={showDiscrepancyDialog} onClose={() => setShowDiscrepancyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningIcon color="warning" />
            <Typography variant="h6" fontWeight={600}>
              Report Discrepancy
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {currentScan && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {condition === 'Damaged'
                  ? 'Items are damaged. Please provide details.'
                  : 'Received quantity differs from expected. Please provide a reason.'}
              </Alert>
              <Typography variant="body2" gutterBottom>
                <strong>Expected:</strong> {currentScan.record.shippedQuantity} units
              </Typography>
              <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                <strong>Received:</strong> {quantityInput} units
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Notes / Reason"
                placeholder="Describe the issue (e.g., damaged in transit, missing items, etc.)"
                value={discrepancyNotes}
                onChange={(e) => setDiscrepancyNotes(e.target.value)}
                required
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDiscrepancyDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmDiscrepancy}
            disabled={!discrepancyNotes.trim()}
            color="warning"
          >
            Report & Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReceiveOrder;
